/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { Logger, useLocalize } from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  InlineMessage,
  Loader,
  Tooltip,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { ModuleDetails } from '../../../../models/PamModels';
import { getAccountListApi } from '../../../../utilities/api/getDataFromApi';
import { msgs } from '../../../../utilities/messages';
import {
  MODAL_PAGE_KINDS,
  PLATFORM_TYPES,
} from '../../../ModalPageForms/constants';
import { ASSET_TYPES } from '../../constants';

import './SpfEndpoint.css';

const PAM_TYPES = {
  CREDENTIAL: 'CREDENTIAL',
  CREDENTIALLESS: 'CREDENTIALLESS',
};

function SpfEndpoint({
  data,
  credentialRequestOnClick,
  dataTestId,
  className,
  isJitForApps,
}) {
  const classes = classnames('SpfEndpoint', className);
  const [accounts, setAccounts] = useState([]);
  const [credentialedArray, setCredentialedArray] = useState([]);
  const [credentiallessArray, setCredentiallessArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);
  const localize = useLocalize();

  const isDBorServer =
    data?.assetType === ASSET_TYPES.DATABASE ||
    data?.assetType === ASSET_TYPES.SERVER;

  const isVault = data?.platformType === PLATFORM_TYPES.VAULT;

  const isCredentialed = credentialedArray.length > 0;

  const isCredentialless =
    (accounts.length >= 0 && credentiallessArray.length >= 0 && isDBorServer) ||
    credentiallessArray.length > 0;

  const parseAccountsForPamType = () => {
    const credArray = [];
    const credlessArray = [];

    setIsLoading(true);
    setIsError(false);
    getAccountListApi(data?.endpointKey)
      .then((response) => {
        if (response.result.accounts.length > 0) {
          setAccounts(response.result.accounts);

          response.result.accounts.forEach((account) => {
            if (account.pamType?.toUpperCase() === PAM_TYPES.CREDENTIAL) {
              credArray.push(account);
            } else if (
              account.pamType?.toUpperCase() === PAM_TYPES.CREDENTIALLESS
            ) {
              credlessArray.push(account);
            }
          });
        }

        setCredentialedArray(credArray);
        setCredentiallessArray(credlessArray);

        setIsLoading(false);
      })
      .catch((error) => {
        Logger.error(
          'An error occurred while fetching the account list:',
          error
        );
        setIsLoading(false);
        setIsError(true);
        setIsErrorMessageVisible(true);
      });
  };

  useEffect(() => {
    parseAccountsForPamType();

    return () => {
      setCredentialedArray([]);
    };
  }, [data]);

  const renderBothButtons = () => (
    <Box tag='div' className='SpfEndpoint-twoButtons'>
      {!isVault && (
        <Button
          type='button'
          kind='outlined'
          className='SpfEndpoint-twoButtons--buttonStyle'
          onClick={() => credentialRequestOnClick(MODAL_PAGE_KINDS.CTS)}
          leftIcon={
            <Icon kind='Connect' size='smallMedium' color='neutral-100' />
          }>
          {localize(msgs.pam.sidePanel.connectToSession)}
        </Button>
      )}
      <Tooltip
        trigger={
          <Button
            type='button'
            kind='outlined'
            className='SpfEndpoint-twoButtons--buttonStyle'
            isDisabled={
              isError || (credentialedArray.length <= 0 && !isLoading)
            }
            isLoading={isLoading}
            loaderText={localize(msgs.pam.sidePanel.checkoutCredential)}
            onClick={() => credentialRequestOnClick(MODAL_PAGE_KINDS.COC)}
            leftIcon={
              <Icon
                kind={isError ? 'Add' : 'PasswordCredentialUnlocked'}
                size='smallMedium'
                color='neutral-100'
              />
            }>
            {localize(msgs.pam.sidePanel.checkoutCredential)}
          </Button>
        }
        text={localize(msgs.pam.sidePanel.checkoutCredentialButtonTooltip)}
        isDisabled={!isError || isCredentialed}
        placement='top'
      />
    </Box>
  );

  const renderButtonsForApplication = () =>
    isError ? (
      <InlineMessage
        kind='background'
        text={localize(msgs.pam.sidePanel.assetDidntLoadError)}
        colorTheme='critical'
        leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        isVisible={isErrorMessageVisible}
        onDismiss={() => setIsErrorMessageVisible(false)}
        isDismissible
        size='small'
      />
    ) : (
      <Box tag='div' className='SpfEndpoint-twoButtons'>
        {isCredentialless && !isVault && !isJitForApps && (
          <Button
            type='button'
            kind='outlined'
            className='SpfEndpoint-twoButtons--buttonStyle'
            onClick={() => credentialRequestOnClick(MODAL_PAGE_KINDS.CTS)}
            leftIcon={
              <Icon kind='Connect' size='smallMedium' color='neutral-100' />
            }>
            {localize(msgs.pam.sidePanel.connectToSession)}
          </Button>
        )}
        {isCredentialed && (
          <Button
            type='button'
            kind='outlined'
            className='SpfEndpoint-twoButtons--buttonStyle'
            onClick={() => credentialRequestOnClick(MODAL_PAGE_KINDS.COC)}
            leftIcon={
              <Icon
                kind='PasswordCredentialUnlocked'
                size='smallMedium'
                color='neutral-100'
              />
            }>
            {localize(msgs.pam.sidePanel.checkoutCredential)}
          </Button>
        )}
      </Box>
    );

  return (
    <Box className={classes} dataTestId={dataTestId}>
      {isLoading && !isDBorServer ? (
        <Box tag='div' className='SpfEndpoint-loader'>
          <Loader kind='dots' format='inline' color='info' size='medium' />
        </Box>
      ) : isDBorServer ? (
        renderBothButtons()
      ) : (
        renderButtonsForApplication()
      )}
    </Box>
  );
}

SpfEndpoint.propTypes = {
  data: ModuleDetails,
  dataTestId: PropTypes.string,
  credentialRequestOnClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  isJitForApps: PropTypes.bool,
};

SpfEndpoint.defaultProps = {
  data: null,
  dataTestId: null,
  className: null,
  isJitForApps: false,
};

export default SpfEndpoint;
